import React, {useEffect, useState} from 'react';
import {ActiveSidebarSectionInfo} from './sidebar/sidebar';
import {IArticleDto} from '../../../api/knowledge-base-api/IArticleDto';
import {useIntl} from '../../../hooks/use-intl';
import {useHistory, useRouteMatch} from 'react-router-dom';
import {KnowledgeBaseApi} from '../../../api/knowledge-base-api/knowledge-base-api';
import {EditArticleForm} from './edit-article-form';
import {Toolbar} from '../../../components/card-toolbar/Toolbar';
import {Routes} from '../../../../configs/routes';
import {ApiRequestException} from '../../../api/axios-instance';
import {toast} from 'react-toastify';
import {IChapterDto} from '../../../api/knowledge-base-api/IChapterDto';
import {InputSelect} from '../../../components/Inputs/InputSelect';
import {useLoading} from '../../../hooks/use-loading';
import {Preloader} from '../../../components/preloader';
import {ValidationErrorsType} from '../../../utils/utils';
import {EXCEPTION_TYPE} from '../../../api/exceptions/BaseException';
import {ValidationException} from '../../../api/exceptions/ValidationException';
import {FormattedMessage} from 'react-intl';
import {AppLocale} from '../../../../localization';

type Props = {
  chapters: Array<IChapterDto>;
  fetchSidebarInfo: () => Promise<any>;
  setActiveSidebarSection: (data: ActiveSidebarSectionInfo) => void;
};

export const EditArticleSection: React.FC<Props> = ({fetchSidebarInfo, setActiveSidebarSection, chapters}) => {
  const intl = useIntl();
  const history = useHistory();
  const match = useRouteMatch<{id: string}>();
  const matchedArticleId = Number(match.params.id);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorsType>(null);

  const [loadings, startLoading, stopLoading] = useLoading({
    content: true,
  });
  const api = new KnowledgeBaseApi();
  const [article, setArticle] = useState<IArticleDto | null>(null);

  const updateArticle = (key: keyof IArticleDto, value: any) => {
    setArticle(prevState => ({...(prevState as IArticleDto), [key]: value}));
  };

  useEffect(() => {
    fetchArticle().then();
  }, [matchedArticleId]);

  useEffect(() => {
    if (article) {
      setActiveSidebarSection({
        type: 'edit',
        payload: {
          chapterId: article.chapter_id,
          articleId: article.id,
        },
      });
    }
  }, [article?.id]);

  const fetchArticle = async () => {
    try {
      startLoading('content');
      const result = await api.getArticle(matchedArticleId);
      setArticle(result.data.item);
    } catch (e) {
      const err = e as ApiRequestException;
      toast.error(e.message || err.errorMessage || intl.formatMessage({id: 'UNEXPECTED_ERROR'}));
    } finally {
      stopLoading('content');
    }
  };

  const handleSaveArticle = async () => {
    try {
      if (!article) {
        // noinspection ExceptionCaughtLocallyJS
        throw new Error('Article not be null');
      }
      await api.updateArticle(article.id, article);
      await fetchSidebarInfo();
      history.push(Routes.getSpecifyArticleRoute(article.id));
    } catch (e) {
      const err = e as ApiRequestException;
      if (err.errorType === EXCEPTION_TYPE.VALIDATION_EXCEPTION) {
        setValidationErrors((err.innerException as ValidationException).error_data.messages);
      } else {
        toast.error(e.message || err.errorMessage || intl.formatMessage({id: 'UNEXPECTED_ERROR'}));
      }
    }
  };

  if (loadings.content) {
    return <Preloader />;
  }

  return (
    <>
      <div className={'card card-custom'}>
        <div className={'card-header'}>
          <div className={'card-title'}>
            <FormattedMessage id={'EDIT'} />
          </div>
        </div>
        <div className={'card-body'}>
          <EditArticleForm article={article ?? {}} update={updateArticle} validationErrors={validationErrors} />
          <InputSelect
            label={intl.formatMessage({id: 'EDIT_CHAPTER'})}
            value={article?.chapter_id}
            options={chapters.map(ch => ({
              label: intl.locale === AppLocale.RU ? ch.title_ru : ch.title_en,
              value: ch.id,
            }))}
            onChange={value => updateArticle('chapter_id', Number(value))}
          />
        </div>
        <div className={'card-footer'}>
          <Toolbar
            items={[
              {
                type: 'BUTTON',
                title: intl.formatMessage({id: 'SAVE'}),
                className: 'btn btn-light-success',
                onClick: handleSaveArticle,
              },
            ]}
          />
        </div>
      </div>
    </>
  );
};
